import { showAppAlert } from '../common/appDialog';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { db, auth } from '../../firebaseConfig';
import { collection, query, orderBy, limit, onSnapshot, where, getDoc, doc, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { ShieldCheckIcon, ShieldExclamationIcon, ArrowPathIcon, MapIcon, ChevronRightIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import { DocumentArrowDownIcon, ClockIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import UnifiedMap from './UnifiedMap';
import { sanitizeFirestoreData } from './reports/ReportShared';
import type { CountryRiskData, Page, GeopoliticalEvent, RiskEvent } from '@types';
import { generateIntelligenceBrief } from '../utils/pdfGenerator';
import RiskTrendIcon from './analytics/RiskTrendIcon';
import ReportAreaPicker from './ReportAreaPicker';
import PagedRiskEventFeed from './PagedRiskEventFeed';
import { COUNTRY_PIN_LIMIT, eventCountry, formatEventRiskScore, matchesFeed, normalizeFeedEvent, publishedEvent } from '../utils/riskEventFeed';
import { getMapCoordinates } from '../utils/mapCoordinates';
import { formatEventConfidenceLevel } from '../utils/eventConfidence';
import { saveDailyReportArea, useDailyReportArea } from '../hooks/useDailyReportArea';

const initialViewState = { longitude: 0, latitude: 20, zoom: 2 };
const theme = "light";

// ─── Source UI Config ─────────────────────────────────────────────────────────

type SourceKey = 'GDELT' | 'GDACS' | 'RELIEFWEB' | 'HDX' | 'NPR' | 'GUARDIAN' | 'ACAPS' | 'AI_BATCH' | string;

const SOURCE_CONFIG: Record<string, { label: string; color: string }> = {
    GDELT:     { label: 'GDELT Intel',        color: 'bg-blue-100 text-blue-800 border-blue-200' },
    GDACS:     { label: 'GDACS Alert',        color: 'bg-teal-100 text-teal-800 border-teal-200' },
    RELIEFWEB: { label: 'ReliefWeb',          color: 'bg-green-100 text-green-800 border-green-200' },
    HDX:       { label: 'HDX Data',           color: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
    NPR:       { label: 'NPR World',          color: 'bg-slate-100 text-slate-700 border-slate-200' },
    GUARDIAN:  { label: 'The Guardian',       color: 'bg-slate-100 text-slate-700 border-slate-200' },
    ACAPS:     { label: 'ACAPS Humanitarian', color: 'bg-orange-100 text-orange-800 border-orange-200' },
    AI_BATCH:  { label: 'AI Analysis',        color: 'bg-violet-100 text-violet-800 border-violet-200' },
};

const CATEGORY_BORDER: Record<string, string> = {
    'Conflict':           'border-l-red-500',
    'Terrorism':          'border-l-red-600',
    'Civil Unrest':       'border-l-yellow-500',
    'Economic':           'border-l-blue-500',
    'Natural Disaster':   'border-l-orange-500',
    'Humanitarian':       'border-l-amber-500',
    'Complex Crisis':     'border-l-amber-600',
};

const CATEGORY_FILTERS = ['All', 'Conflict', 'Humanitarian', 'Natural Disaster', 'Economic', 'Civil Unrest'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getSourceConfig(source: string) {
    return SOURCE_CONFIG[source?.toUpperCase()] || { label: source || 'Intelligence', color: 'bg-slate-100 text-slate-600 border-slate-200' };
}

function getCategoryBorder(category: string): string {
    for (const [key, cls] of Object.entries(CATEGORY_BORDER)) {
        if (category?.toLowerCase().includes(key.toLowerCase())) return cls;
    }
    return 'border-l-slate-300';
}

function timeAgo(processedAt: any): string {
    if (!processedAt) return '';
    const ms = typeof processedAt.toMillis === 'function'
        ? processedAt.toMillis()
        : (processedAt instanceof Date ? processedAt.getTime() : new Date(processedAt).getTime());
    const diff = Date.now() - ms;
    if (diff < 60_000) return 'Just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
}

// ─── Component Interface ──────────────────────────────────────────────────────

export interface RiskMonitorDashboardProps {
    db: any;
    currentUser?: any;
    currentOrganization?: any;
    isLoaded: boolean;
    interactive?: boolean;
    riskData: any[];
    riskEvents: RiskEvent[];
    choroplethEnabled?: boolean;
    onNavigate: (nav: { page: Page; params?: any }) => void;
    onCountryClick?: (iso3: string) => void;
    filterIso3?: string | null;
    countryName?: string;
    lockCountry?: boolean;
    allCountryPins?: boolean;
    externalFocus?: { lat: number; lng: number; zoom?: number } | null;
    readOnly?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const RiskMonitorDashboard: React.FC<RiskMonitorDashboardProps> = ({
    db,
    currentUser,
    currentOrganization,
    isLoaded,
    interactive = true,
    riskData,
    riskEvents = [],
    choroplethEnabled = true,
    onNavigate,
    onCountryClick,
    filterIso3,
    countryName,
    lockCountry = false,
    allCountryPins = false,
    externalFocus,
    readOnly = false,
}) => {
    const feedScrollRef = useRef<HTMLDivElement>(null);
    const [localSelectedCountry, setLocalSelectedCountry] = useState<string | null>(filterIso3 || null);
    const [events, setEvents]       = useState<RiskEvent[]>([]);
    const [pinEvents, setPinEvents] = useState<RiskEvent[]>([]);
    const [pinError, setPinError] = useState('');
    const [pinRetry, setPinRetry] = useState(0);
    const [selectedFeedEvent, setSelectedFeedEvent] = useState<RiskEvent | null>(null);
    const [filter, setFilter]       = useState<'all' | 'high'>('all');
    const [categoryFilter, setCategoryFilter] = useState<string>('All');
    const [focusedLocation, setFocusedLocation] = useState<{lat: number, lng: number, zoom?: number} | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const mapRef = useRef<any>(null);
    const activeTarget = localSelectedCountry;
    const isCountryMode = !!activeTarget;
    const reportArea = useDailyReportArea(activeTarget, currentOrganization?.id);
    const [reportError, setReportError] = useState('');
    const activeReportCountry = useRef(activeTarget);
    activeReportCountry.current = activeTarget;

    useEffect(() => { setLocalSelectedCountry(filterIso3 || null); }, [filterIso3]);
    useEffect(() => { if (externalFocus) setFocusedLocation(externalFocus); }, [externalFocus]);

    // Pins have a separate, bounded country query. Global never fetches pins.
    useEffect(() => {
        let cancelled = false;
        setPinEvents([]);
        setPinError('');
        if (!activeTarget || !db)
            return;
        const unsubscribe = onSnapshot(query(collection(db, 'risk_events'),
            where('geo.country_iso3', '==', activeTarget), orderBy('processedAt', 'desc'),
            ...(allCountryPins ? [] : [limit(COUNTRY_PIN_LIMIT)])), snapshot => {
            if (!cancelled)
                setPinEvents(snapshot.docs.filter(doc => publishedEvent(doc.data())).map(doc => normalizeFeedEvent(doc.id, doc.data())));
        }, () => { if (!cancelled) setPinError('Country pins could not load. Retry to load them.'); });
        return () => { cancelled = true; unsubscribe(); };
    }, [db, activeTarget, pinRetry, allCountryPins]);

    // ── Category filter applied to the risk feed ──
    const displayedEvents = useMemo(() => {
        return events.filter(event => matchesFeed(event, filter === 'high', categoryFilter));
    }, [events, categoryFilter, filter]);

    // Helper to log generated reports
    const saveReportMetadata = async (type: string, country: string, title: string, resultData: any) => {
        if (!auth.currentUser?.email || !currentOrganization?.id) return;
        try {
            await addDoc(collection(db, "generated_reports"), {
                type, country, title,
                organizationId: currentOrganization.id,
                resultData: sanitizeFirestoreData(resultData),
                timestamp:  serverTimestamp(),
                userEmail:  auth.currentUser.email,
                userName:   auth.currentUser.displayName || auth.currentUser.email
            });
        } catch (e) { console.error("Error logging report metadata:", e); }
    };

    const handleDailyMicro = useCallback(async () => {
        const targetCountry = localSelectedCountry;
        if (!targetCountry || !onNavigate) return;
        setIsGenerating(true);
        setReportError('');
        try {
            if (reportArea.area.subdivisionCodes.length) {
                let report = reportArea.report;
                if (!report || reportArea.refreshError || Date.now() - Date.parse(report.generatedAt) >= 86_400_000)
                    report = (await saveDailyReportArea(reportArea.area)).report || undefined;
                if (!report)
                    throw new Error('The regional report is unavailable.');
                if (activeReportCountry.current !== targetCountry)
                    return;
                onNavigate({ page: 'CountryAnalysisResults', params: { reportData: report, country: targetCountry, isDailyMicro: true } });
                await saveReportMetadata('daily_micro', targetCountry, report.title, report);
                return;
            }
            const docRef = doc(db, "daily_country_analysis", targetCountry);
            const snap   = await getDoc(docRef);
            if (activeReportCountry.current !== targetCountry)
                return;
            if (snap.exists()) {
                onNavigate({ page: 'CountryAnalysisResults', params: { reportData: sanitizeFirestoreData(snap.data()), country: targetCountry, alerts: events, isDailyMicro: true } });
                await saveReportMetadata('daily_micro', targetCountry, `Daily Micro Analysis: ${targetCountry}`, snap.data());
            } else {
                void showAppAlert(`No daily micro report found for ${targetCountry}. Please contact Intelligence Operations for on-demand tactical briefs.`);
            }
        } catch (e) {
            console.error("Fetch Error:", e);
            setReportError('Could not open the daily report. Please retry.');
        }
        finally { setIsGenerating(false); }
    }, [localSelectedCountry, filterIso3, onNavigate, db, events, reportArea.area, reportArea.report, reportArea.refreshError, currentOrganization]);

    const handleMacroReport = useCallback(async () => {
        const targetCountry = localSelectedCountry;
        if (!targetCountry || !onNavigate) return;
        setIsGenerating(true);
        try {
            // UNIFIED PIPELINE: Route through triggerManualCountryAnalysis.
            // This is the same Firebase Function used by the GG Suite "Generate Country Analysis" card.
            // It provides routed AI, OSINT pre-fetch, and the full dossier schema.
            const functions = getFunctions(undefined, 'us-central1');
            const triggerComprehensive = httpsCallable(functions, 'triggerManualCountryAnalysis');
            const result = await triggerComprehensive({
                country: targetCountry
            });
            if (result.data) {
                const macroData = sanitizeFirestoreData((result.data as any).data);
                onNavigate({ 
                    page: 'CountryAnalysisResults', 
                    params: { 
                        reportData: macroData, 
                        country: targetCountry,
                        iso3: (result.data as any).iso3,
                        // BOUNDARY ENFORCEMENT: Explicitly mark as comprehensive — NOT a Daily Micro
                        activeWorkflowId: 'Comprehensive Country Geopolitical & Risk Analysis',
                        isDailyMicro: false,
                    } 
                });
                await saveReportMetadata('macro', targetCountry, `Comprehensive Geopolitical Report: ${targetCountry}`, macroData);
            }
        } catch (error) { console.error("Comprehensive Analysis Engine Error:", error); }
        finally { setIsGenerating(false); }
    }, [localSelectedCountry, filterIso3, onNavigate]);

    const handleCountryClickInternal = useCallback((iso: string | null) => {
        if (lockCountry)
            return;
        setLocalSelectedCountry(iso && iso !== 'Global' ? iso : null);
        setSelectedFeedEvent(null);
        setFocusedLocation(null);
        mapRef.current?.clearSelection();
        if (onCountryClick) onCountryClick(iso || '');
    }, [onCountryClick, lockCountry]);

    const getRiskColor = (score: number) => {
        if (score >= 80) return 'text-red-700 bg-red-50 border-red-200';
        if (score >= 60) return 'text-orange-700 bg-orange-50 border-orange-200';
        if (score >= 40) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
        return 'text-green-700 bg-green-50 border-green-200';
    };

    const getRiskDot = (score: number) => {
        if (score >= 80) return 'bg-red-500';
        if (score >= 60) return 'bg-orange-500';
        if (score >= 40) return 'bg-yellow-400';
        return 'bg-green-500';
    };

    // E1: mapAlerts now derived from displayedEvents so category pills filter the map too
    const mapAlerts = useMemo(() => {
        if (!activeTarget) return [];
        const selected = selectedFeedEvent && eventCountry(selectedFeedEvent) === activeTarget ? [selectedFeedEvent] : [];
        const points = [...new Map([...selected, ...pinEvents].filter(event => eventCountry(event) === activeTarget && matchesFeed(event, filter === 'high', categoryFilter)).map(event => [event.id, event])).values()];
        if (!allCountryPins)
            points.length = Math.min(points.length, COUNTRY_PIN_LIMIT);
        return points.map(e => ({
        ...e,
        id: e.id,
        title: e.title,
        summary: e.summary,
        category: e.category,
        riskScore: e.riskScore,
        sentiment: -1,
        timestamp: (e.processedAt as any)?.toMillis?.() || Date.now(),
        geo: {
            lat: e.location?.lat || 0,
            lng: e.location?.lng || 0,
            country_iso3: e.location?.country || 'GLB',
            location_name: e.location?.city || 'Global'
        }
    }));
    }, [activeTarget, pinEvents, selectedFeedEvent, filter, categoryFilter, allCountryPins]);

    const criticalCount = displayedEvents.filter(e => (e.riskScore ?? 0) >= 80).length;
    const highCount     = displayedEvents.filter(e => (e.riskScore ?? 0) >= 60 && (e.riskScore ?? 0) < 80).length;

    return (
        <div className="flex flex-col w-full h-full bg-white relative">

            {/* ── Header ── */}
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 flex-shrink-0">
                <div>
                    <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                        <ShieldExclamationIcon className="h-5 w-5 text-indigo-600" />
                        {lockCountry ? `${countryName || activeTarget} Risk Map` : 'Global Risk Map'}
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">
                        {activeTarget
                            ? `🎯 Country Intel Mode: ${activeTarget}`
                            : 'Real-time Insights grounded global risk & security intelligence'}
                    </p>
                </div>

                <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                        {!readOnly && !lockCountry && activeTarget && (
                            <>
                                <button
                                    onClick={handleDailyMicro}
                                    disabled={isGenerating || reportArea.loading || reportArea.saving || !!reportArea.error}
                                    className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-all shadow-sm disabled:opacity-50 whitespace-nowrap"
                                >
                                    {isGenerating ? 'Fetching...' : `Daily Micro (${reportArea.area.subdivisionCodes.length ? `${reportArea.area.subdivisionCodes.length} selected areas` : activeTarget})`}
                                </button>
                            </>
                        )}
                        {!readOnly && <button
                            onClick={() => generateIntelligenceBrief({
                                countryName: activeTarget || 'Global Feed',
                                iso3:        activeTarget || 'GLB',
                                riskScore: displayedEvents[0]?.riskScore ?? null
                            }, displayedEvents)}
                            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-all shadow-sm whitespace-nowrap"
                        >
                            <DocumentArrowDownIcon className="h-4 w-4" />
                            <span>Intelligence Brief</span>
                        </button>}
                    </div>

                    {!lockCountry && <div className="flex gap-2">
                        <button
                            onClick={() => { handleCountryClickInternal(null); setFilter('all'); setCategoryFilter('All'); }}
                            className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all border ${!activeTarget && filter === 'all' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'}`}
                        >
                            Global Feed
                        </button>
                        <button
                            onClick={() => setFilter('high')}
                            className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all border ${filter === 'high' && !activeTarget ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-500/20' : 'bg-white text-slate-400 border-slate-200 hover:border-indigo-300 hover:text-indigo-500'}`}
                        >
                            Critical Only
                        </button>
                        <button
                            onClick={() => handleCountryClickInternal(null)}
                            className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all border bg-white text-slate-400 border-slate-200 hover:border-rose-300 hover:text-rose-500"
                        >
                            Clear Selection
                        </button>
                    </div>}
                </div>
            </div>

            {!readOnly && !lockCountry && currentOrganization?.id && <ReportAreaPicker
                key={activeTarget || 'global'}
                area={reportArea.area}
                loading={reportArea.loading}
                canSave={reportArea.canSave && !isGenerating && !reportArea.saving}
                error={reportArea.error}
                onRetry={reportArea.retry}
                onCountryChange={country => {
                    handleCountryClickInternal(country || null);
                    setReportError('');
                }}
                onSave={reportArea.save}
            />}
            {!readOnly && reportError && <p role="alert" className="px-6 py-2 text-sm text-red-600">{reportError}</p>}
            {!readOnly && reportArea.refreshError && <p role="status" className="px-6 py-2 text-sm text-amber-700">{reportArea.refreshError}</p>}

            {/* ── Intelligence Update Banner ── */}
            <div className="bg-blue-600 px-6 py-2 flex items-center justify-between shadow-inner">
                <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-1.5 rounded-full border border-white/30 backdrop-blur-sm shadow-inner flex items-center justify-center">
                        <InformationCircleIcon className="h-4 w-4 text-blue-100" />
                    </div>
                    <p className="text-[11px] font-black text-white uppercase tracking-tighter sm:tracking-normal leading-tight">
                        The Insights Engine refreshes global risk information and daily country analysis reports daily.
                        <span className="hidden sm:inline opacity-70 ml-2 font-medium normal-case tracking-normal">Please reach out if you require further analysis assistance.</span>
                    </p>
                </div>
                <div className="hidden md:flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse border border-emerald-300 shadow-[0_0_8px_rgba(52,211,153,0.5)]"></span>
                        <span className="text-[9px] font-black text-blue-100 uppercase tracking-widest whitespace-nowrap">Engine Synchronized</span>
                    </div>
                </div>
            </div>

            {/* ── Content Area — Split View ── */}
            <div className="flex-grow flex flex-col lg:flex-row overflow-hidden">

                {/* MAP PANEL (65%) */}
                <div className="h-[300px] lg:h-full lg:w-[65%] border-b lg:border-b-0 lg:border-r border-slate-200 relative flex flex-col">
                    <div className="flex-grow relative">
                        <UnifiedMap
                            ref={mapRef}
                            isLoaded={isLoaded}
                            initialViewState={initialViewState}
                            theme={theme as "light" | "dark"}
                            interactive={interactive}
                            choroplethEnabled={choroplethEnabled}
                            riskData={riskData}
                            filterIso3={(activeTarget) || undefined}
                            alerts={mapAlerts}
                            countryPinsOnly
                            allCountryPins={allCountryPins}
                            readOnly={readOnly}
                            focusedLocation={focusedLocation}
                            activeCategory={categoryFilter}
                            onCountryClick={handleCountryClickInternal}
                            onNavigate={onNavigate}
                            hideSidebar={true}
                        />
                        {/* Map Overlay Stats */}
                        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-md border border-slate-200 text-xs z-10 space-y-1.5">
                            <div className="font-bold text-slate-700 mb-1">Loaded feed events</div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.6)]"></span>
                                <span className="font-semibold text-slate-800">{criticalCount}</span>
                                <span className="text-slate-500">Critical</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-orange-500 shadow-[0_0_6px_rgba(249,115,22,0.5)]"></span>
                                <span className="font-semibold text-slate-800">{highCount}</span>
                                <span className="text-slate-500">High</span>
                            </div>
                        </div>
                    </div>

                    {/* Risk Legend */}
                    <div className="bg-slate-50 border-t border-slate-200 p-3 flex items-center justify-between text-[10px] text-slate-500">
                        <div className="flex items-center gap-4">
                            <span className="font-bold uppercase tracking-wider text-slate-400">Risk Legend:</span>
                            <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-600"></span><span className="font-medium">80+ Critical</span></div>
                            <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-orange-500"></span><span className="font-medium">60–80 High</span></div>
                            <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-yellow-400"></span><span className="font-medium">40–60 Moderate</span></div>
                            <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-500"></span><span className="font-medium">0–40 Low</span></div>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                            <span className="font-mono">Live Intelligence Synchronized</span>
                        </div>
                    </div>
                </div>

                {/* FEED PANEL (35%) */}
                <div className="flex-grow lg:w-[35%] bg-slate-50 flex flex-col min-h-0">

                    {/* Feed Header */}
                    <div className="p-3 border-b border-slate-200 bg-white shadow-sm z-10 flex-shrink-0">
                        <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                                <h3 className="text-xs font-extrabold text-indigo-900 uppercase tracking-widest">Command Intel Feed</h3>
                                {isCountryMode && (
                                    <span className="px-2 py-0.5 bg-indigo-600 text-white text-[9px] font-black rounded uppercase tracking-wider">
                                        {activeTarget}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-[9px] text-slate-400 font-medium">{displayedEvents.length} events</span>
                                <div className="flex items-center gap-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                    <span className="text-[9px] font-bold text-slate-400">LIVE</span>
                                </div>
                            </div>
                        </div>

                        {/* Category Filter Pills */}
                        <div className="flex gap-1 flex-wrap">
                            {CATEGORY_FILTERS.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setCategoryFilter(cat)}
                                    className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider transition-all border ${
                                        categoryFilter === cat
                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                            : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-300 hover:text-indigo-600'
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div ref={feedScrollRef} className="overflow-y-auto overscroll-contain flex-grow min-h-0" tabIndex={0} aria-label="Command intelligence feed">

                        {/* ── Intelligence Feed Section Header ── */}
                        <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                            <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                {isCountryMode
                                    ? `Intelligence Feed — ${activeTarget}`
                                    : 'Real-time Intelligence Nodes'}
                            </h4>
                            {isCountryMode && !lockCountry && (
                                <button
                                    onClick={() => handleCountryClickInternal(null)}
                                    className="text-[9px] text-slate-400 hover:text-rose-500 font-medium transition-colors flex items-center gap-1"
                                >
                                    <GlobeAltIcon className="h-3 w-3" /> Global View
                                </button>
                            )}
                        </div>

                        {/* ── Event Cards ── */}
                        {pinError && <p role="alert" className="p-3 text-sm text-red-700">{pinError} <button onClick={() => setPinRetry(value => value + 1)} className="underline">Retry pins</button></p>}
                        <p className="px-3 py-2 text-xs text-slate-500">{activeTarget ? allCountryPins ? `${pinEvents.length} available events mapped in ${countryName || activeTarget}.` : `Up to ${COUNTRY_PIN_LIMIT} recent events mapped in ${activeTarget}.` : 'Select a country or event to load map pins.'}</p>
                        <PagedRiskEventFeed scrollContainerRef={feedScrollRef} key={`${activeTarget}:${filter}:${categoryFilter}`} database={db} country={activeTarget} critical={filter === 'high'} category={categoryFilter} onEvents={setEvents}
                            renderEvent={(riskEvent) => {
                                    const src = getSourceConfig((riskEvent as any).source);
                                    const catBorder = getCategoryBorder(riskEvent.category || '');
                                    const ago = timeAgo(riskEvent.processedAt);
                                    const score = riskEvent.riskScore ?? 0;

                                    return (
                                        <div
                                            key={riskEvent.id}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.currentTarget.click(); } }}
                                            onClick={() => {
                                                const country = eventCountry(riskEvent);
                                                if (country && country !== activeTarget) handleCountryClickInternal(country);
                                                setSelectedFeedEvent(riskEvent);
                                                const coordinates = getMapCoordinates(riskEvent);
                                                setFocusedLocation(coordinates ? { ...coordinates, zoom: 8 } : null);
                                                if (mapRef.current) mapRef.current.showEventDetail(riskEvent);
                                            }}
                                            className={`p-4 hover:bg-slate-50/80 transition-all border-l-4 ${catBorder} cursor-pointer group`}
                                        >
                                            {/* Row 1: Source + Risk Score + Time */}
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider border ${src.color}`}>
                                                        {src.label}
                                                    </span>
                                                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${getRiskColor(score)}`}>
                                                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${getRiskDot(score)} mr-1 align-middle`}></span>
                                                        {formatEventRiskScore(riskEvent)}
                                                    </span>
                                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-slate-50 text-slate-500 border border-slate-100 capitalize">
                                                        {riskEvent.category || 'General'}
                                                    </span>
                                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                                        Confidence: {formatEventConfidenceLevel(riskEvent)}{riskEvent.vettingStatus === 'published_low_confidence' ? ' · Unverified' : ''}
                                                    </span>
                                                </div>
                                                {ago && (
                                                    <span className="text-[9px] text-slate-400 flex items-center gap-0.5 flex-shrink-0">
                                                        <ClockIcon className="h-2.5 w-2.5" />{ago}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Row 2: Title */}
                                            <h3 className="text-sm font-semibold text-slate-800 mb-1.5 leading-snug group-hover:text-indigo-700 transition-colors">
                                                {riskEvent.title}
                                            </h3>

                                            {/* Row 3: Summary */}
                                            <p className="text-xs text-slate-500 line-clamp-3 mb-2 leading-relaxed">
                                                {riskEvent.summary}
                                            </p>
                                            <p className="text-[10px] text-slate-500 mb-2">{riskEvent.sourceNote}</p>

                                            {/* Row 4: Location + Threat Actor */}
                                            <div className="flex items-center justify-between gap-2 text-[10px] text-slate-400">
                                                <div className="flex items-center gap-1 min-w-0">
                                                    <MapIcon className="h-3 w-3 flex-shrink-0" />
                                                    <span className="truncate">
                                                        {riskEvent.location?.city || 'Unknown'}, {riskEvent.location?.country || 'UNK'}
                                                    </span>
                                                </div>
                                                {(riskEvent as any).primary_threat_actor && (riskEvent as any).primary_threat_actor !== 'Unknown' && (
                                                    <span className="flex-shrink-0 px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-100 font-medium truncate max-w-[120px]">
                                                        ⚠ {(riskEvent as any).primary_threat_actor}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Row 5: Verification Statement */}
                                            {riskEvent.verification?.statement && (
                                                <div className="mt-2 pt-2 border-t border-slate-100">
                                                    <p className="text-[10px] text-indigo-600/80 font-medium italic leading-snug line-clamp-2">
                                                        {riskEvent.verified ? '✓ ' : 'Unverified · '}{riskEvent.verification.statement}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    );
                            }} />
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-4 py-1.5 border-t border-slate-200 flex justify-between items-center text-[10px] text-slate-400 flex-shrink-0">
                <span>Powered by routed Gemini models · GDELT · ACAPS · GDACS</span>
                <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                    Monitoring
                </span>
            </div>
        </div>
    );
};
